//#region IMPORTS
import { Fragment } from "react";
import { CheckIcon, MinusIcon } from "@heroicons/react/20/solid";
import classNames from "../../helpers/classNames";
//#endregion

//#region DATA TYPES
type CellValue = boolean | string;

interface Tier {
  name: string;
  id: string;
}

interface Feature {
  name: string;
  note?: string;
  tiers: Record<string, CellValue>;
}

interface Section {
  name: string;
  features: Feature[];
}

interface RulesTable {
  /** Heading shown above the comparison grid. */
  title: string;
  /** Short intro sentence under the heading. */
  intro: string;
  /** Columns of the grid. */
  tiers: Tier[];
  /** Grouped rows. */
  sections: Section[];
  /** Footnotes rendered under the grid. */
  footnotes?: string[];
}
//#endregion

//#region RULES DATA
// These mirror the gating hooks (useReadOnlyPreset / useBulkActionsAvailable /
// useShowRowActions) and the bulk-action control panels. Keep in sync with
// src/ORDER_STATUS_BULK_ACTIONS.md.

// Promo orders — what each preset unlocks.
const promoByPreset: RulesTable = {
  title: "Promo orders — by preset",
  intro:
    "Same flow as instore — Approve → Manage → Send — with Backorder / Original as overview filters on the right.",
  tiers: [
    { name: "Approve", id: "promo-approve" },
    { name: "Manage", id: "promo-manage" },
    { name: "Send", id: "promo-send" },
    { name: "Backorder + promo", id: "promo-backorder" },
    { name: "Original", id: "promo-readonly" },
  ],
  sections: [
    {
      name: "Access",
      features: [
        {
          name: "Who sees it",
          tiers: {
            Approve: "HQ / Admin",
            Manage: "Picker / Admin",
            Send: "Picker / Admin",
            "Backorder + promo": "Picker / Admin",
            "Original": "All roles",
          },
        },
        {
          name: "Status",
          tiers: {
            Approve: "Pending",
            Manage: "Ordered",
            Send: "In progress",
            "Backorder + promo": "optional",
            "Original": "—",
          },
        },
        {
          name: "Row selection (checkboxes)",
          note: "Backorder needs a promo code (status optional).",
          tiers: {
            Approve: true,
            Manage: true,
            Send: true,
            "Backorder + promo": true,
            "Original": false,
          },
        },
        {
          name: "Pagination",
          tiers: {
            Approve: "Client-side",
            Manage: "Client-side",
            Send: "Client-side",
            "Backorder + promo": "Client-side",
            "Original": "Server-side",
          },
        },
      ],
    },
    {
      name: "Bulk buttons",
      features: [
        {
          name: "Approve multiple",
          tiers: {
            Approve: true,
            Manage: false,
            Send: false,
            "Backorder + promo": false,
            "Original": false,
          },
        },
        {
          name: "Set in progress",
          tiers: {
            Approve: false,
            Manage: true,
            Send: false,
            "Backorder + promo": "if Ordered",
            "Original": false,
          },
        },
        {
          name: "Ship order(s)",
          tiers: {
            Approve: false,
            Manage: false,
            Send: true,
            "Backorder + promo": "if In progress",
            "Original": false,
          },
        },
        {
          name: "Request list(s)",
          note: "Needs backorder + a promo code (status optional).",
          tiers: {
            Approve: false,
            Manage: "if promo",
            Send: "if promo",
            "Backorder + promo": "if promo",
            "Original": false,
          },
        },
      ],
    },
  ],
  footnotes: [
    "Flow pills mirror instore: Approve (emerald) → Manage (cog, amber) → Send (truck, sky), grouped with chevrons; Backorder / Original sit on the right.",
    "Promo approve is HQ / admin only — pickers do not approve promo orders (no Approve pill, no bulk approve, no per-row approve/decline; the ?preset=approve URL redirects a picker back to their own flow). Approve pings for HQ; admin gets a calm pill. Instore approve is still HQ / admin / picker — this picker restriction applies to promo orders only.",
    "Backorder is a plain quick-filter pill (no dropdown) and the default landing. On the Backorder path, selection (checkboxes + client-side) needs backorder + a promo code (status optional); the flow presets (Approve/Manage/Send) keep selection without a promo.",
    "Request list(s) (was \"Download picklists\") is per promo: it needs order_type = backorder + a promo code (status optional), for picker / admin. The button shows on Backorder / Manage / Send with a how-to tooltip and is disabled until backorder + promo + a selection are set.",
    "The \"All\" preset was removed — promo always lands on Backorder, and any reference to all resolves to backorder. Original is a read-only overview.",
    "Cancelled is terminal: filtering on Cancelled hides the selection column (no select-all, checkboxes or bulk buttons) and both the Decline and Actions columns — nothing applies.",
    "The Decline column only applies to pending orders: it is hidden from In progress onwards (In progress, Shipped, Cancelled) and on any page with no pending order to approve. The Actions column stays for shipping, except on Cancelled where it is dropped too.",
    "Selection is kept in Redux, so it survives page changes within a preset.",
    "Shop users (no Admin / Picker / HQ role) only get the read-only overviews (Backorder / Original) — no checkboxes, no bulk buttons, no per-row actions. Any flow preset (Approve / Manage / Send) redirects them back to the overview.",
  ],
};

// Promo orders — which bulk button applies to a selected order's status.
const promoByStatus: RulesTable = {
  title: "Order status → bulk button",
  intro:
    "Once selection is available, each control-panel button only acts on the orders that qualify by status.",
  tiers: [
    { name: "Pending", id: "status-pending" },
    { name: "Ordered", id: "status-ordered" },
    { name: "In progress", id: "status-inprogress" },
    { name: "Shipped", id: "status-shipped" },
    { name: "Cancelled", id: "status-cancelled" },
  ],
  sections: [
    {
      name: "Bulk buttons",
      features: [
        {
          name: "Approve multiple (HQ / Admin)",
          tiers: {
            Pending: true,
            Ordered: false,
            "In progress": false,
            Shipped: false,
            Cancelled: false,
          },
        },
        {
          name: "Set in progress (Picker / Admin)",
          note: "Hidden while the filter is already set to In progress.",
          tiers: {
            Pending: false,
            Ordered: true,
            "In progress": false,
            Shipped: false,
            Cancelled: false,
          },
        },
        {
          name: "Ship order(s) (Picker / Admin)",
          note: "Orders must be set to In progress before they can be shipped.",
          tiers: {
            Pending: false,
            Ordered: false,
            "In progress": true,
            Shipped: false,
            Cancelled: false,
          },
        },
        {
          name: "Request list(s) (Picker / Admin)",
          note: "Gated by backorder + promo (not the row status); runs on the whole selection.",
          tiers: {
            Pending: true,
            Ordered: true,
            "In progress": true,
            Shipped: true,
            Cancelled: false,
          },
        },
      ],
    },
  ],
  footnotes: [
    "Fulfilment flow (Picker / Admin): Ordered → In progress → Shipped. An order must be set to In progress before it can be shipped.",
    "Orders of type Original never qualify for any bulk action.",
    "Shipped and Cancelled are terminal — no bulk actions apply.",
    "Bulk actions only ask for confirmation above 5 selected items (BULK_CONFIRM_THRESHOLD); smaller batches run immediately.",
  ],
};

// Instore orders — what each preset unlocks.
const instoreByPreset: RulesTable = {
  title: "Instore orders — by preset",
  intro:
    "Fulfilment runs Approve → Manage (set in progress) → Send (ship) → Deliver (set delivered) → Archive (archive in bulk). Each loads a single high-limit page so selection works without paging.",
  tiers: [
    { name: "All / Week / Opening", id: "instore-overview" },
    { name: "Manage", id: "instore-manage" },
    { name: "Send", id: "instore-send" },
    { name: "Deliver", id: "instore-deliver" },
    { name: "Archive", id: "instore-archive" },
    { name: "Approve", id: "instore-approve" },
  ],
  sections: [
    {
      name: "Access",
      features: [
        {
          name: "Who sees it",
          tiers: {
            "All / Week / Opening": "All roles",
            Manage: "Picker / Admin",
            Send: "Picker / Admin",
            Deliver: "Picker / Admin",
            Archive: "Picker / Admin",
            Approve: "HQ / Admin / Picker",
          },
        },
        {
          name: "Status shown",
          tiers: {
            "All / Week / Opening": "—",
            Manage: "Ordered",
            Send: "In progress",
            Deliver: "Shipped",
            Archive: "Delivered",
            Approve: "Pending",
          },
        },
        {
          name: "Row selection (checkboxes)",
          tiers: {
            "All / Week / Opening": false,
            Manage: true,
            Send: true,
            Deliver: true,
            Archive: true,
            Approve: true,
          },
        },
      ],
    },
    {
      name: "Bulk buttons",
      features: [
        {
          name: "Approve multiple",
          tiers: {
            "All / Week / Opening": false,
            Manage: false,
            Send: false,
            Deliver: false,
            Archive: false,
            Approve: true,
          },
        },
        {
          name: "Set in progress",
          tiers: {
            "All / Week / Opening": false,
            Manage: true,
            Send: false,
            Deliver: false,
            Archive: false,
            Approve: false,
          },
        },
        {
          name: "Ship order(s)",
          tiers: {
            "All / Week / Opening": false,
            Manage: false,
            Send: true,
            Deliver: false,
            Archive: false,
            Approve: false,
          },
        },
        {
          name: "Set delivered",
          tiers: {
            "All / Week / Opening": false,
            Manage: false,
            Send: false,
            Deliver: true,
            Archive: false,
            Approve: false,
          },
        },
        {
          name: "Archive multiple",
          tiers: {
            "All / Week / Opening": false,
            Manage: false,
            Send: false,
            Deliver: false,
            Archive: true,
            Approve: false,
          },
        },
        {
          name: "Export multiple (ZIP, shown first)",
          tiers: {
            "All / Week / Opening": false,
            Manage: true,
            Send: true,
            Deliver: true,
            Archive: true,
            Approve: false,
          },
        },
      ],
    },
  ],
  footnotes: [
    "Flow: Manage (Ordered) → set in progress → Send (In progress) → ship → Deliver (Shipped) → set delivered → Archive (Delivered) → archive. Manage pill icon is a cog, Send a truck, Deliver a check badge, Archive a box. Each carries a count and is disabled at 0.",
    "Manage is one preset shown to picker and admin (the legacy admin \"handle\" key resolves to the same filter).",
    "Badges stay in sync: the instore tab badge and the sidebar \"orders\" badge equal the sum of the flow pills the role sees — HQ: Approve; picker / admin: Approve + Manage + Send.",
    "Picker can now approve instore orders too (Approve pill, bulk approve, per-row approve/decline, decline-toggle column) — same as admin.",
    "On the overview presets (All / Week / Opening), picker / admin can pick a status in the filter bar to unlock high-limit, client-side multiselect — Export on every status, plus Set delivered (on Shipped) and Archive multiple (on Delivered / Cancelled).",
    "Pills ping (kaki pulse) only for the role whose workflow it is — picker for Approve + Manage + Send, HQ for Approve. Admin gets a calm white pill.",
    "HQ columns: no Decline column; the Cancel column stays; the Actions column only shows on the Approve preset.",
    "Per-row: lime Export icon (left of Actions), amber cog (set in progress, on Ordered), sky truck (ship, on In progress), archive box (on Shipped / Delivered / Cancelled) — picker / admin.",
    "Shop users (no Admin / Picker / HQ role) only get the read-only overviews (All / Week / Opening), scoped to their own store — no checkboxes, no bulk buttons, no per-row actions. Any flow preset (Approve / Manage / Send / Deliver / Archive) redirects them back to the overview.",
  ],
};

const tables: RulesTable[] = [promoByPreset, promoByStatus, instoreByPreset];
//#endregion

//#region CELL
function RuleCell({ value }: { value: CellValue | undefined }) {
  if (typeof value === "string") {
    return (
      <div className="text-center text-sm/6 text-gray-600">{value}</div>
    );
  }
  return (
    <Fragment>
      {value === true ? (
        <CheckIcon
          aria-hidden="true"
          className="mx-auto h-5 w-5 text-emerald-700"
        />
      ) : (
        <MinusIcon aria-hidden="true" className="mx-auto h-5 w-5 text-gray-300" />
      )}
      <span className="sr-only">{value === true ? "Yes" : "No"}</span>
    </Fragment>
  );
}
//#endregion

//#region COMPARISON TABLE
function ComparisonTable({ table }: { table: RulesTable }) {
  const { title, intro, tiers, sections, footnotes } = table;

  return (
    <section className="mt-20 first:mt-0">
      <div className="max-w-3xl">
        <h3 className="text-2xl font-semibold tracking-tight text-gray-900">
          {title}
        </h3>
        <p className="mt-2 text-base text-gray-600">{intro}</p>
      </div>

      {/* xs to lg — stacked cards per column */}
      <div className="mt-8 space-y-8 lg:hidden">
        {tiers.map((tier) => (
          <div
            key={tier.id}
            className="rounded-xl ring-1 ring-gray-200 p-6"
          >
            <h4 className="text-sm font-semibold text-gray-900">{tier.name}</h4>
            <ul className="mt-6 space-y-6 text-sm/6 text-gray-900">
              {sections.map((section) => (
                <li key={section.name}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    {section.name}
                  </p>
                  <ul className="mt-3 space-y-3">
                    {section.features.map((feature) => {
                      const value = feature.tiers[tier.name];
                      return (
                        <li
                          key={feature.name}
                          className="flex items-start gap-x-3"
                        >
                          {typeof value === "string" ? (
                            <span className="mt-0.5 h-5 w-5 shrink-0" />
                          ) : value === true ? (
                            <CheckIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
                          ) : (
                            <MinusIcon className="mt-0.5 h-5 w-5 shrink-0 text-gray-300" />
                          )}
                          <span>
                            {feature.name}
                            {typeof value === "string" && (
                              <span className="text-gray-500"> — {value}</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* lg+ — comparison grid */}
      <div className="mt-10 hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <caption className="sr-only">{title}</caption>
          <thead>
            <tr className="border-b border-gray-200">
              <th scope="col" className="w-1/4 py-4 pr-6" />
              {tiers.map((tier) => (
                <th
                  key={tier.id}
                  scope="col"
                  className="px-6 py-4 text-center text-sm font-semibold text-gray-900"
                >
                  {tier.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((section, sectionIdx) => (
              <Fragment key={section.name}>
                <tr>
                  <th
                    scope="colgroup"
                    colSpan={tiers.length + 1}
                    className={classNames(
                      sectionIdx === 0 ? "pt-8" : "pt-10",
                      "pb-3 text-sm font-semibold text-emerald-700",
                    )}
                  >
                    {section.name}
                  </th>
                </tr>
                {section.features.map((feature) => (
                  <tr key={feature.name} className="border-b border-gray-100">
                    <th
                      scope="row"
                      className="py-4 pr-6 align-top text-sm font-normal text-gray-900"
                    >
                      {feature.name}
                      {feature.note && (
                        <span className="mt-0.5 block text-xs text-gray-400">
                          {feature.note}
                        </span>
                      )}
                    </th>
                    {tiers.map((tier) => (
                      <td key={tier.id} className="px-6 py-4 align-top">
                        <RuleCell value={feature.tiers[tier.name]} />
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footnotes */}
      {footnotes && footnotes.length > 0 && (
        <ul className="mt-8 space-y-2 text-sm text-gray-500 list-disc pl-5">
          {footnotes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
//#endregion

export default function OrderRulesPage() {
  //#region RENDER
  return (
    <div className="bg-white py-16">
      <div className="w-full px-8">
        <div className="max-w-3xl">
          <h2 className="text-base/7 font-semibold text-emerald-700">
            Reference
          </h2>
          <p className="mt-2 text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
            Order bulk-action rules
          </p>
          <p className="mt-6 text-lg text-gray-600">
            How order status, preset and role decide which bulk actions and row
            columns appear across the promo and instore order lists. This page
            is admin-only and mirrors the gating in the app.
          </p>
        </div>

        {tables.map((table) => (
          <ComparisonTable key={table.title} table={table} />
        ))}
      </div>
    </div>
  );
  //#endregion
}
